// "檢查" tab (F-10, D-55): runs the automatic quality check for one version and lists the suspicious segments by
// category; the segments are also drawn on the timeline. Clicking one jumps to its most suspicious frame and puts
// the crosshair where it is strongest.

import type { App } from '../app/app';
import type { SourceId } from '../core/types';
import { slotLabel } from '../core/types';
import { QualityManager, type QualityResult } from '../analysis/quality-manager';
import { QUALITY_CATS, cellCenter, type Finding, type QualityCat, type Sensitivity } from '../core/quality';
import { tickOfAFrame } from '../core/timeline';
import { kvGet, kvSet } from '../app/persist';
import { t } from '../i18n';
import { $, downloadBlob, esc, setText, toast } from './dom';
import { fsAccess } from '../app/persist';
import { AI_ANALYSIS_ENABLED } from '../ai/enabled';

let qm: QualityManager;
let target: SourceId | null = null;
const hidden = new Set<QualityCat>();
const open = new Set<QualityCat>();
let lastKey = '';
let sheetBusy = false;

const MAX_ROWS = 60;

export function initQuality(app: App): void {
  qm = new QualityManager(app);
  (app as unknown as { quality: QualityManager }).quality = qm;
  qm.onChange = () => app.store.notify();
  // The last chosen sensitivity is remembered on this computer; medium until one is chosen.
  void kvGet<Sensitivity>('qualitySensitivity').then((s) => {
    if (s && s !== qm.sensitivity) qm.setSensitivity(s);
  });
  $('#qcRun').addEventListener('click', () => {
    const id = currentTarget(app);
    if (id) void qm.check(id);
  });
  $('#qcCancel').addEventListener('click', () => qm.cancel());
  // F-10.9: the findings as screenshot sheets, saved in the project folder (or a folder chosen now).
  $('#qcSheet').addEventListener('click', async () => {
    const id = currentTarget(app);
    const r = id ? qm.results.get(id) : undefined;
    if (!id || !r || !r.findings.length || sheetBusy) return;
    sheetBusy = true;
    app.store.notify();
    try {
      const { findingSheets } = await import('../export/finding-sheet');
      const base = app.state.settings.frameNumberBase;
      const pages = await findingSheets(app, id, r, (f) => findingLabel(f, base), (f) => frameRange(f, base), (d, n) =>
        toast(t('sheet.progress', { d, n }), 1500),
      );
      let dir = await app.projectFolder(true);
      if (!dir && fsAccess.supported) dir = await fsAccess.pickDirectory('readwrite');
      for (const p of pages) {
        if (dir) {
          const h = await dir.getFileHandle(p.name, { create: true });
          const w = await h.createWritable();
          await w.write(p.blob);
          await w.close();
        } else downloadBlob(p.blob, p.name);
      }
      toast(t('sheet.done', { n: pages.length, folder: dir?.name ?? t('sheet.downloads') }), 6000);
    } catch (e) {
      if ((e as DOMException).name !== 'AbortError') toast(t('export.failed', { detail: String(e) }), 6000);
    } finally {
      sheetBusy = false;
      app.store.notify();
    }
  });
  // F-10.10: what the AI edit was meant to do (kept with the shot) and the AI look itself.
  const brief = $<HTMLInputElement>('#qcBrief');
  brief.addEventListener('change', () => app.store.project((p) => (p.aiBrief = brief.value.trim()), { undo: false }));
  brief.addEventListener('keydown', (e) => e.stopPropagation());
  $('#qcAi').addEventListener('click', async () => {
    const id = currentTarget(app);
    if (!id) return;
    try {
      const n = await qm.aiLook(id);
      lastKey = '';
      toast(t('ai.done', { n }), 5000);
    } catch (e) {
      const key = (e as { errorKey?: string }).errorKey ?? (e instanceof Error && e.message.startsWith('ai.') ? e.message : '');
      if ((e as DOMException).name === 'AbortError') return;
      if (key === 'ai.error.noKey') {
        toast(t(key), 6000);
        $('#btnSettings').click();
      } else toast(t('ai.failed', { reason: key ? t(key) : String(e) }), 8000);
    }
  });
  // F-10.8: every shot of the project, one after the other.
  $('#qcAll').addEventListener('click', async () => {
    const r = await qm.checkProject();
    toast(t('quality.allDone', { n: r.checked, s: r.skipped }), 6000);
  });
  const sens = $<HTMLSelectElement>('#qcSens');
  sens.addEventListener('change', () => {
    qm.setSensitivity(sens.value as Sensitivity);
    void kvSet('qualitySensitivity', sens.value);
  });
  $('#qcTarget').addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-src]');
    if (!b) return;
    target = b.dataset.src as SourceId;
    app.store.notify();
  });
  $('#qcResults').addEventListener('click', (e) => {
    const el = e.target as HTMLElement;
    const jump = el.closest<HTMLElement>('[data-jump]');
    if (jump) return jumpTo(app, Number(jump.dataset.jump));
    const vis = el.closest<HTMLElement>('[data-vis]');
    if (vis) {
      const c = vis.dataset.vis as QualityCat;
      if (hidden.has(c)) hidden.delete(c);
      else hidden.add(c);
      lastKey = '';
      return app.store.notify();
    }
    const spark = el.closest<SVGElement>('[data-spark]');
    if (spark && e instanceof MouseEvent) {
      const r = spark.getBoundingClientRect();
      const res = qm.results.get(currentTarget(app)!);
      if (!res) return;
      const f = res.first + Math.round(((e.clientX - r.left) / r.width) * (res.n - 1));
      return seekFrame(app, f);
    }
    const head = el.closest<HTMLElement>('[data-cat]');
    if (head) {
      const c = head.dataset.cat as QualityCat;
      if (open.has(c)) open.delete(c);
      else open.add(c);
      lastKey = '';
      app.store.notify();
    }
  });
  $('#aiResults').addEventListener('click', (e) => {
    const jump = (e.target as HTMLElement).closest<HTMLElement>('[data-jump]');
    if (jump) jumpTo(app, Number(jump.dataset.jump));
  });
  $('#qualityMarks').addEventListener('pointerdown', (e) => {
    const m = (e.target as HTMLElement).closest<HTMLElement>('[data-jump]');
    if (!m) return;
    e.stopPropagation();
    jumpTo(app, Number(m.dataset.jump));
  });
}

function currentTarget(app: App): SourceId | null {
  const bs = app.sources.loadedBs();
  if (target && bs.includes(target)) return target;
  const sel = app.project.layout.selectedB;
  return bs.includes(sel) ? sel : (bs[0] ?? null);
}

function seekFrame(app: App, frame: number): void {
  if (app.player.playing) app.player.pause();
  app.player.seek(tickOfAFrame(app.sources.timeline, frame));
}

/** Finding index into the displayed result's list. */
function jumpTo(app: App, index: number): void {
  const r = qm.results.get(currentTarget(app)!);
  const f = r?.findings[index];
  if (!r || !f) return;
  const id = r.source;
  if (app.project.layout.selectedB !== id) app.store.project((p) => (p.layout.selectedB = id), { undo: false });
  seekFrame(app, f.peak);
  // Faces have an exact place; other findings point at the middle of the region where they are strongest.
  if (f.box) app.setCrosshair({ x: f.box.x + f.box.w / 2, y: f.box.y + f.box.h / 2 });
  else if (f.at) app.setCrosshair(f.at);
  else if (f.cell !== null) app.setCrosshair(cellCenter(f.cell, r.area));
}

const pct = (v: number) => (v * 100).toFixed(v > 0 && v < 0.1 ? 1 : 0);

export function findingLabel(f: Finding, base: 0 | 1): string {
  switch (f.cat) {
    case 'ai':
      return (f.value >= 3 ? '‼ ' : f.value >= 2 ? '! ' : '') + (f.text || t('quality.k.ai.none'));
    case 'frames':
      if (f.kind === 'duplicate' && (f.count ?? 1) > 1) return t('quality.k.dupRun', { n: f.count ?? 1, b: (f.bFrame ?? 0) + base });
      return t('temporal.ev.' + f.kind, { n: f.count ?? 1, b: (f.bFrame ?? 0) + base });
    case 'face':
      if (f.kind.startsWith('drift.')) return t('quality.k.face.drift', { part: t('quality.part.' + f.kind.slice(6)), v: (f.value * 100).toFixed(0) });
      return t('quality.k.face.' + f.kind, { v: f.kind === 'turned' ? '' : (f.value * 100).toFixed(0) });
    case 'motion':
      return f.kind === 'freeze' ? t('quality.k.freeze', { p: pct(f.value) }) : t('quality.k.jitter', { x: f.value.toFixed(1) });
    case 'color':
      return t('quality.k.' + f.kind, { v: f.kind === 'sat' ? pct(f.value) : f.value.toFixed(1) });
    case 'clip':
      return t('quality.k.' + f.kind, { v: pct(f.value) });
    case 'seam':
    case 'flicker':
      return t(`quality.k.${f.cat}.${f.kind}`, { v: f.value.toFixed(1) });
    default:
      return t('quality.k.' + f.cat);
  }
}

function frameRange(f: Finding, base: 0 | 1): string {
  return f.start === f.end ? `F ${f.start + base}` : `F ${f.start + base}–${f.end + base}`;
}

function sparkline(curve: Float32Array): string {
  const n = curve.length;
  if (!n) return '';
  const m = Math.min(n, 300);
  const pts: string[] = [];
  for (let i = 0; i < m; i++) {
    let v = 0;
    const s = Math.floor((i * n) / m);
    const e = Math.max(s + 1, Math.floor(((i + 1) * n) / m));
    for (let k = s; k < e; k++) if (curve[k] > v) v = curve[k];
    pts.push(`${i},${(3 - Math.min(3, v)).toFixed(2)}`);
  }
  return `<svg class="qc-spark" data-spark viewBox="0 0 ${Math.max(1, m - 1)} 3" preserveAspectRatio="none"><line x1="0" x2="${m}" y1="2" y2="2" class="thr"/><polyline points="${pts.join(' ')}"/></svg>`;
}

/**
 * Every check item is always listed (owner, 2026-10-01): before a check "—", after it ✓ when nothing was found or
 * the number of findings (click to open the list). Hovering the name tells what the item looks for.
 */
function resultsHtml(app: App, r: QualityResult | undefined): string {
  const base = app.state.settings.frameNumberBase;
  let html = '';
  if (r && !qm.current(r.source)) html += `<div class="warn-item">${esc(t('quality.stale'))}</div>`;
  if (r && r.offsetHint !== null) html += `<div class="warn-item">${esc(t('quality.offsetHint', { n: (r.offsetHint > 0 ? '+' : '') + r.offsetHint }))}</div>`;
  if (r && !r.findings.length) html += `<div class="qc-none">${esc(t('quality.none'))}</div>`;
  for (const cat of QUALITY_CATS) {
    // The Analysis results have their own tab (F-10.10).
    if (cat === 'ai') continue;
    const list = r ? r.findings.map((f, i) => ({ f, i })).filter((x) => x.f.cat === cat) : [];
    const skipped = !!r && cat === 'frames' && !r.framesChecked;
    const tip = skipped ? t('temporal.onlyOneToOne') : t('quality.desc.' + cat);
    if (!list.length) {
      const state = !r || skipped ? '—' : '✓';
      html +=
        `<div class="qc-cat empty"><div class="qc-head"><span class="qc-dot qc-${cat} off"></span>` +
        `<span class="qc-name" title="${esc(tip)}">${esc(t('quality.cat.' + cat))}</span><span class="qc-count${state === '✓' ? ' ok' : ''}">${state}</span><span class="qc-caret"></span></div></div>`;
      continue;
    }
    const isOpen = open.has(cat);
    html +=
      `<div class="qc-cat${isOpen ? ' open' : ''}">` +
      `<div class="qc-head" data-cat="${cat}"><button class="qc-dot qc-${cat}${hidden.has(cat) ? ' off' : ''}" data-vis="${cat}" title="${esc(t('quality.visTip'))}"></button>` +
      `<span class="qc-name" title="${esc(tip)}">${esc(t('quality.cat.' + cat))}</span><span class="qc-count">${list.length}</span><span class="qc-caret">${isOpen ? '▾' : '▸'}</span></div>` +
      (cat === 'frames' || !r ? '' : sparkline(r.curves[cat])) +
      (isOpen
        ? `<div class="qc-list">` +
          list
            .slice(0, MAX_ROWS)
            .map(({ f, i }) => `<div class="qc-row"><button data-jump="${i}">${esc(frameRange(f, base))}</button><span>${esc(findingLabel(f, base))}</span></div>`)
            .join('') +
          (list.length > MAX_ROWS ? `<div class="muted small">${esc(t('quality.more', { n: list.length - MAX_ROWS }))}</div>` : '') +
          `</div>`
        : '') +
      `</div>`;
  }
  return html;
}

/** Analysis tab (F-10.10): the problems the model reported for the displayed version, or a hint when there are none. */
function aiResultsHtml(app: App, r: QualityResult | undefined): string {
  const base = app.state.settings.frameNumberBase;
  const list = r ? r.findings.map((f, i) => ({ f, i })).filter((x) => x.f.cat === 'ai') : [];
  if (!list.length) return `<div class="muted small">${esc(t('ai.empty'))}</div>`;
  return (
    `<div class="qc-list">` +
    list.map(({ f, i }) => `<div class="qc-row"><button data-jump="${i}">${esc(frameRange(f, base))}</button><span>${esc(findingLabel(f, base))}</span></div>`).join('') +
    `</div>`
  );
}

export function updateQuality(app: App): void {
  if (!qm) return;
  const bs = app.sources.loadedBs();
  const id = currentTarget(app);
  const r = id ? qm.results.get(id) : undefined;
  const running = qm.running;
  const tl = app.sources.timeline;
  const key = JSON.stringify([
    bs,
    id,
    running,
    qm.batch,
    qm.ai,
    app.project.aiBrief,
    sheetBusy,
    app.ws.activeProject()?.shots.length,
    qm.phase,
    Math.round(qm.progress * 100),
    qm.error,
    r ? [r.key, r.findings.length, r.sensitivity, qm.current(r.source)] : null,
    qm.sensitivity,
    [...hidden],
    [...open],
    tl.totalTicks,
    app.state.settings.frameNumberBase,
    t('dock.check'),
  ]);
  if (key === lastKey) return;
  lastKey = key;
  // Version picker, only when there is more than one B.
  const tg = $('#qcTarget');
  const tgHtml = bs.length > 1 ? bs.map((b) => `<button data-src="${b}" class="${b === id ? 'on' : ''}">${slotLabel(b)}</button>`).join('') : '';
  if (tg.innerHTML !== tgHtml) tg.innerHTML = tgHtml;
  tg.hidden = bs.length < 2;
  const run = $<HTMLButtonElement>('#qcRun');
  setText(run, id ? t(r ? 'quality.again' : 'quality.run', { id: slotLabel(id) }) : t('quality.run', { id: 'B' }));
  run.disabled = !!running || !id || !app.sources.has('A');
  const busy = !!running || !!qm.batch || !!qm.ai;
  const aiBtn = $<HTMLButtonElement>('#qcAi');
  aiBtn.disabled = !AI_ANALYSIS_ENABLED || busy || !id || !app.sources.has('A');
  setText($('#qcAiStatus'), !AI_ANALYSIS_ENABLED ? t('ai.paused') : qm.ai ? t('ai.running', { d: qm.ai.done, n: qm.ai.total }) : '');
  const br = $<HTMLInputElement>('#qcBrief');
  br.disabled = !AI_ANALYSIS_ENABLED;
  if (document.activeElement !== br) br.value = app.project.aiBrief ?? '';
  run.hidden = busy;
  const all = $<HTMLButtonElement>('#qcAll');
  all.hidden = busy || (app.ws.activeProject()?.shots.length ?? 0) < 2;
  $('#qcCancel').hidden = !busy;
  $<HTMLSelectElement>('#qcSens').value = qm.sensitivity;
  const pb = app.project.playback;
  $('#qcRange').hidden = pb.inPoint === undefined && pb.outPoint === undefined;
  const prog = $<HTMLProgressElement>('#qcProgress');
  prog.hidden = !running;
  prog.value = qm.progress;
  let status = '';
  if (running) status = t(qm.phase === 'frames' ? 'quality.phaseFrames' : 'quality.phasePixels', { p: Math.round(qm.progress * 100) });
  if (qm.batch) status = t('quality.batch', { i: qm.batch.shot, n: qm.batch.shots }) + (status ? ' · ' + status : '');
  else if (qm.error) status = t('quality.error', { detail: qm.error });
  setText($('#qcStatus'), status);
  $('#qcStatus').hidden = !status;
  $('#qcResults').innerHTML = resultsHtml(app, running ? undefined : r);
  $('#aiResults').innerHTML = aiResultsHtml(app, running ? undefined : r);
  $('#qcSheetRow').hidden = !r || running !== null || !r.findings.length || !qm.current(r.source);
  $<HTMLButtonElement>('#qcSheet').disabled = sheetBusy;
  // Timeline: segments of the displayed version.
  const total = tl.totalTicks;
  const marks = r && qm.current(r.source) ? r.findings.map((f, i) => ({ f, i })).filter((x) => !hidden.has(x.f.cat)) : [];
  const base = app.state.settings.frameNumberBase;
  $('#qualityMarks').innerHTML = marks
    .map(({ f, i }) => {
      const left = (tickOfAFrame(tl, f.start) / total) * 100;
      const width = (((f.end - f.start + 1) * tl.ticksPerFrame) / total) * 100;
      return `<div class="qc-mark qc-${f.cat}" data-jump="${i}" title="${esc(t('quality.cat.' + f.cat) + ' · ' + frameRange(f, base) + ' · ' + findingLabel(f, base))}" style="left:${left}%;width:${width}%"></div>`;
    })
    .join('');
}
